import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QoreTalksShell from './QoreTalksShell';
import QtEmptyState from './QtEmptyState';
import TalkFormModal from './TalkFormModal';
import { fetchTalks, fetchLiveRooms } from '../../services/qoreTalksApi';
import { fmtDuration } from './QoreTalksShell';

const QoreTalksManage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [talks, setTalks] = useState([]);
  const [liveCount, setLiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [all, rooms] = await Promise.all([
        fetchTalks({ limit: 100 }),
        fetchLiveRooms().catch(() => []),
      ]);
      setTalks(all);
      setLiveCount(rooms.length);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const drafts = talks.filter((x) => x.status === 'draft' || x.status === 'scheduled');
  const published = talks.filter((x) => x.status === 'published');

  const Row = ({ talk }) => (
    <div className="qt-manage-row">
      <div>
        <strong style={{ display: 'block', marginBottom: 4 }}>{talk.title}</strong>
        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
          {talk.category} · {talk.status} · {fmtDuration(talk.duration)}
        </span>
      </div>
      <button
        type="button"
        className="qt-btn qt-btn--ghost"
        onClick={() => navigate('/qoretalks/episodes/' + (talk._id || talk.id))}
      >
        {t('qoreTalks.viewAll')}
      </button>
    </div>
  );

  return (
    <QoreTalksShell activeTab="home" mainClass="qt-main--wide" hideNav>
      <div className="qt-manage-hero">
        <nav className="qt-breadcrumb" aria-label="Breadcrumb">
          <Link to="/qoretalks">{t('qoreTalks.pageTitle')}</Link>
          {' / '}
          <span>{t('qoreTalks.manageTitle')}</span>
        </nav>
        <h1>{t('qoreTalks.manageTitle')}</h1>
        <p>{t('qoreTalks.manageSubtitle')}</p>
        <div className="qt-manage-actions">
          <button type="button" className="qt-btn qt-btn--primary" onClick={() => setShowModal(true)}>
            {t('qoreTalks.manageUpload')}
          </button>
          <Link to="/qoretalks/live?create=true" className="qt-btn qt-btn--rose">
            {t('qoreTalks.manageGoLive')}
          </Link>
          <Link to="/qoretalks" className="qt-btn qt-btn--ghost">
            {t('qoreTalks.openPublicHub')}
          </Link>
        </div>
        <div className="qt-stats" style={{ marginTop: '1.25rem' }}>
          <div className="qt-stat">
            <div>
              <div className="qt-stat__value">{published.length}</div>
              <div className="qt-stat__label">{t('qoreTalks.managePublished')}</div>
            </div>
          </div>
          <div className="qt-stat">
            <div>
              <div className="qt-stat__value">{drafts.length}</div>
              <div className="qt-stat__label">{t('qoreTalks.manageDrafts')}</div>
            </div>
          </div>
          <div className="qt-stat">
            <div>
              <div className="qt-stat__value">{liveCount}</div>
              <div className="qt-stat__label">{t('qoreTalks.statLiveNow')}</div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="qt-loading" style={{ minHeight: 200 }}>
          <div className="qt-spinner" />
        </div>
      ) : talks.length === 0 ? (
        <QtEmptyState
          variant="manage"
          actions={
            <>
              <button type="button" className="qt-btn qt-btn--primary" onClick={() => setShowModal(true)}>
                {t('qoreTalks.manageUpload')}
              </button>
              <Link to="/qoretalks/live?create=true" className="qt-btn qt-btn--rose">
                {t('qoreTalks.manageGoLive')}
              </Link>
            </>
          }
        />
      ) : (
        <>
          {drafts.length > 0 && (
            <section className="qt-manage-section">
              <h2>{t('qoreTalks.manageDrafts')}</h2>
              <div className="qt-manage-list">
                {drafts.map((talk) => (
                  <Row key={talk._id || talk.id} talk={talk} />
                ))}
              </div>
            </section>
          )}
          <section className="qt-manage-section">
            <h2>{t('qoreTalks.managePublished')}</h2>
            <div className="qt-manage-list">
              {published.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: '0.875rem' }}>{t('qoreTalks.noTalks')}</p>
              ) : (
                published.map((talk) => <Row key={talk._id || talk.id} talk={talk} />)
              )}
            </div>
          </section>
        </>
      )}

      {showModal && (
        <TalkFormModal isOpen={showModal} onClose={() => setShowModal(false)} onCreated={load} />
      )}
    </QoreTalksShell>
  );
};

export default QoreTalksManage;